import {
  ActivityModel,
  ActivityValue,
  FlatActivityModel,
  ValidationStatus
} from 'types/shared'
import { VALIDATION_STATUSES } from './constants'
import {
  AllowedLocales,
  isValidDateInterval,
  isValidTime,
  timeToSeconds,
  isValidDateTime,
  todayAtBeginningOfDay,
  todayAtEndOfDay,
  secondsFromTime
} from './date'
import { useValidateUsingCallback } from 'modules/interaction/utils/validateUsingCallback'

const DEFAULT_CHARACTER_LIMIT = 2000

const hasValidationCallback = (
  activityModel: Pick<ActivityModel, 'validation_activity_callback'>
) => !!activityModel.validation_activity_callback

const hasInformationRequestCallback = (
  activityModel: Pick<ActivityModel, 'information_request_callback'>
) => !!activityModel.information_request_callback

type ErrorMatcherContext = {
  lang: AllowedLocales
}

type ErrorMatcher = {
  helperText: {
    des: string
    complement?: string
  }
  matcher: (
    value: NonNullable<ActivityModel['value']>,
    activityModel: ActivityModel | FlatActivityModel,
    context: ErrorMatcherContext
  ) => boolean
}

const emptinessErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    const isAutocompleteMultiselection =
      activityModel.content_type === 'autocomplete_multiselection'
    const isEmptyValue =
      value === '' ||
      (activityModel.content_type === 'multiple_answers' && value === '[]') ||
      (activityModel.information_request_callback &&
        typeof value !== 'object' &&
        !isAutocompleteMultiselection)

    return Boolean(activityModel.required && isEmptyValue)
  }
}

const regexErrorMatcher: ErrorMatcher = {
  helperText: { des: 'invalid-format' },
  matcher: (value, activityModel) => {
    if (
      activityModel.content_type === 'date' ||
      activityModel.content_type === 'datetime' ||
      !('validation_regex' in activityModel) ||
      !activityModel.validation_regex
    )
      return false

    if (activityModel.content_type === 'multiple_answers') {
      if (value === '' || value === '[]') return false
      try {
        const items: unknown[] = JSON.parse(
          typeof value === 'string' ? value : '[]'
        )
        const regex = new RegExp(activityModel.validation_regex)
        return items.some((item) => !regex.test(String(item)))
      } catch {
        return false
      }
    }

    return Boolean(
      value !== '' &&
      !new RegExp(activityModel.validation_regex).test(value.toString())
    )
  }
}

const autocompleteErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    const isString = activityModel.content_type === 'string'
    const hasFilteraction = !!activityModel.information_request_filteraction

    const isAutocomplete =
      isString &&
      (hasInformationRequestCallback(activityModel) || hasFilteraction)

    return Boolean(
      isAutocomplete &&
      activityModel.required &&
      (typeof value === 'string' || (!value.id && !value.des))
    )
  }
}

const dateErrorMatcher: ErrorMatcher = {
  helperText: {
    des: 'invalid-date-title',
    complement: 'invalid-date-description'
  },
  matcher: (value, activityModel, { lang }) =>
    (activityModel.content_type === 'date' ||
      activityModel.content_type === 'datetime') &&
    !isValidDateTime({
      dateTime: typeof value === 'string' ? value : '',
      contentType: activityModel.content_type,
      lang
    })
}

const timeErrorMatcher: ErrorMatcher = {
  helperText: {
    des: 'invalid-time-title',
    complement: 'invalid-time-description'
  },
  matcher: (value, activityModel, { lang }) =>
    activityModel.content_type === 'time' &&
    !isValidTime(typeof value === 'string' ? value : '', lang)
}

const minTimeErrorMatcher: ErrorMatcher = {
  helperText: { des: 'min-time-title', complement: 'min-time-description' },
  matcher: (value, activityModel, { lang }) => {
    if (
      activityModel.content_type !== 'time' &&
      !(
        activityModel.content_type === 'datepicker' &&
        activityModel.format === 'time'
      )
    )
      return false

    const minTime = activityModel.valid_min_time
    const minFromNow = activityModel.min_from_now

    if (!minTime && !minFromNow) return false

    const valueStr = typeof value === 'string' ? value : ''
    const valueSeconds = timeToSeconds(valueStr, lang)

    let minSeconds: number | null = null
    if (minFromNow) {
      const now = new Date()
      minSeconds = secondsFromTime({
        hour: now.getHours(),
        minute: now.getMinutes(),
        second: now.getSeconds()
      })
    } else if (minTime) {
      minSeconds = timeToSeconds(minTime, lang)
    }

    if (minSeconds === null) return false

    return valueSeconds === null || valueSeconds < minSeconds
  }
}

const maxTimeErrorMatcher: ErrorMatcher = {
  helperText: { des: 'max-time-title', complement: 'max-time-description' },
  matcher: (value, activityModel, { lang }) => {
    if (
      activityModel.content_type !== 'time' &&
      !(
        activityModel.content_type === 'datepicker' &&
        activityModel.format === 'time'
      )
    )
      return false

    const maxTime = activityModel.valid_max_time
    const maxFromNow = activityModel.max_from_now

    if (!maxTime && !maxFromNow) return false

    const valueStr = typeof value === 'string' ? value : ''
    const valueSeconds = timeToSeconds(valueStr, lang)

    let maxSeconds: number | null = null
    if (maxFromNow) {
      const now = new Date()
      maxSeconds = secondsFromTime({
        hour: now.getHours(),
        minute: now.getMinutes(),
        second: now.getSeconds()
      })
    } else if (maxTime) {
      maxSeconds = timeToSeconds(maxTime, lang)
    }

    if (maxSeconds === null) return false

    return valueSeconds === null || valueSeconds > maxSeconds
  }
}

const documentErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    let parsedValue: { url?: string } = {
      url: ''
    }
    try {
      parsedValue = JSON.parse(
        typeof value === 'string' ? value : '{ "url": "" }'
      )
    } catch (e) {}

    return Boolean(
      (activityModel.content_type === 'document_photo' ||
        activityModel.content_type === 'document_video' ||
        activityModel.content_type === 'document_audio') &&
      !parsedValue.url &&
      activityModel.required
    )
  }
}

const evaluationErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) =>
    Boolean(
      activityModel.content_type === '10_rating_evaluation' &&
      activityModel.required &&
      Number(typeof value === 'string' ? value : value.des) <= 0
    )
}

const otpErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) =>
    activityModel.content_type === 'one_time_password' &&
    (typeof value === 'object' ? value.des : value)?.replaceAll(' ', '')
      ?.length !== activityModel.length
}

const starsEvaluationErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) =>
    Boolean(
      activityModel.content_type === 'stars_evaluation' &&
      activityModel.required &&
      Number(typeof value === 'string' ? value : value.des) <= 0
    )
}

const agreementTermErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    const isAgreementTerm = activityModel.content_type === 'agreement_term'

    if (!isAgreementTerm) return false

    const isAgreementTermChecked =
      typeof value === 'string' && JSON.parse(value)?.checked === '1'

    return isAgreementTerm && !isAgreementTermChecked
  }
}

const drawErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    const isDraw = activityModel.content_type === 'draw'

    if (!isDraw || !activityModel.required) return false

    const isDrawChecked =
      typeof value === 'string' && !!JSON.parse(value)?.base64

    return isDraw && !isDrawChecked
  }
}

const minDateErrorMatcher: ErrorMatcher = {
  helperText: { des: 'min-date-title', complement: 'min-date-description' },
  matcher: (value, activityModel, { lang }) => {
    if (
      activityModel.content_type !== 'date' &&
      activityModel.content_type !== 'datetime'
    ) {
      return false
    }

    let minFromNow = activityModel.min_from_now && new Date().toISOString()

    if (activityModel.content_type === 'date' && minFromNow)
      minFromNow = todayAtBeginningOfDay()

    const minYear = activityModel.minimum_year
      ? new Date(`${activityModel.minimum_year}-01-01T00:00:00`).toISOString()
      : undefined

    if (minFromNow || minYear) {
      return !isValidDateInterval({
        dateTime: typeof value === 'string' ? value : '',
        minDate: minFromNow || minYear,
        maxDate: undefined,
        lang
      })
    }

    return false
  }
}

const maxDateErrorMatcher: ErrorMatcher = {
  helperText: { des: 'max-date-title', complement: 'max-date-description' },
  matcher: (value, activityModel, { lang }) => {
    if (
      activityModel.content_type !== 'date' &&
      activityModel.content_type !== 'datetime'
    ) {
      return false
    }

    let maxFromNow = activityModel.max_from_now && new Date().toISOString()

    if (activityModel.content_type === 'date' && maxFromNow)
      maxFromNow = todayAtEndOfDay()

    const maxYear = activityModel.maximum_year
      ? new Date(`${activityModel.maximum_year}-12-31T23:59:59`).toISOString()
      : undefined

    if (maxFromNow || maxYear) {
      return !isValidDateInterval({
        dateTime: typeof value === 'string' ? value : '',
        minDate: undefined,
        maxDate: maxFromNow || maxYear,
        lang
      })
    }

    return false
  }
}

const characterLimitErrorMatcher: ErrorMatcher = {
  helperText: { des: 'character-limit-exceeded' },
  matcher: (value, activityModel) => {
    if (activityModel.content_type === 'multiple_answers') {
      if (
        !('character_limit' in activityModel) ||
        !activityModel.character_limit
      )
        return false
      try {
        const items: unknown[] = JSON.parse(
          typeof value === 'string' ? value : '[]'
        )
        return items.some(
          (item) => String(item).length > activityModel.character_limit!
        )
      } catch {
        return false
      }
    }

    const isTextArea = activityModel.content_type === 'textarea'
    const hasFilteraction = !!activityModel.information_request_filteraction
    const isString =
      activityModel.content_type === 'string' &&
      !hasValidationCallback(activityModel) &&
      !hasFilteraction

    return (
      (isTextArea || isString) &&
      typeof value === 'string' &&
      value.length > (activityModel.character_limit || DEFAULT_CHARACTER_LIMIT)
    )
  }
}

const minAnswersErrorMatcher: ErrorMatcher = {
  helperText: { des: 'min-answers' },
  matcher: (value, activityModel) => {
    if (
      activityModel.content_type !== 'multiple_answers' ||
      !activityModel.min_answers
    )
      return false
    try {
      const parsed = JSON.parse(typeof value === 'string' ? value : '[]')
      return Array.isArray(parsed) && parsed.length < activityModel.min_answers
    } catch {
      return false
    }
  }
}

const maxAnswersErrorMatcher: ErrorMatcher = {
  helperText: { des: 'max-answers' },
  matcher: (value, activityModel) => {
    if (
      activityModel.content_type !== 'multiple_answers' ||
      !activityModel.max_answers
    )
      return false
    try {
      const parsed = JSON.parse(typeof value === 'string' ? value : '[]')
      return Array.isArray(parsed) && parsed.length > activityModel.max_answers
    } catch {
      return false
    }
  }
}

const errorMatchers = [
  emptinessErrorMatcher,
  regexErrorMatcher,
  autocompleteErrorMatcher,
  dateErrorMatcher,
  timeErrorMatcher,
  minTimeErrorMatcher,
  maxTimeErrorMatcher,
  documentErrorMatcher,
  evaluationErrorMatcher,
  otpErrorMatcher,
  starsEvaluationErrorMatcher,
  agreementTermErrorMatcher,
  drawErrorMatcher,
  minDateErrorMatcher,
  maxDateErrorMatcher,
  characterLimitErrorMatcher,
  minAnswersErrorMatcher,
  maxAnswersErrorMatcher
]

export type ValidateFlatActivityValueProps = {
  activityModel: FlatActivityModel
  value?: ActivityValue
  skipCallback?: boolean
}

export type UseValidateActivityValueProps = {
  context: ErrorMatcherContext
}

export const useValidateActivityValue = ({
  context
}: UseValidateActivityValueProps) => {
  const validateUsingCallback = useValidateUsingCallback()

  const validateFlatActivityValue = async ({
    activityModel,
    value,
    skipCallback = false
  }: ValidateFlatActivityValueProps): Promise<{
    validationStatus: ValidationStatus
    helperText?: {
      des: string
      complement?: string
    }
  }> => {
    if (value === undefined) {
      return { validationStatus: VALIDATION_STATUSES.PENDING }
    }

    const errorHelperText = errorMatchers.find(({ matcher }) =>
      matcher(value, activityModel, context)
    )?.helperText

    if (errorHelperText) {
      return {
        validationStatus: VALIDATION_STATUSES.INVALID,
        helperText: errorHelperText
      }
    }

    const shouldCallValidationCallback =
      hasValidationCallback(activityModel) && value !== ''
    const isEmptyOptionalValue = value === '' && !activityModel.required

    if (!shouldCallValidationCallback && !isEmptyOptionalValue) {
      return {
        validationStatus: VALIDATION_STATUSES.VALID,
        helperText: { des: 'activity-input.validation.is_valid' }
      }
    }

    if (shouldCallValidationCallback && !skipCallback) {
      return validateUsingCallback({ ...activityModel, value })
    }

    return { validationStatus: 'pending' }
  }

  return { validateFlatActivityValue }
}
